import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import { Button } from '../components/ui/button';
import { FeatherArrowLeft, FeatherCalendar } from '../components/ui/icons';
import { Badge } from '../components/ui/badge';
import BunnyLoader from '../components/ui/BunnyLoader';
import { getMemberWorkPlanHistory } from '../services/managerService';
import './WorkPlanCalendar.css';

const WorkPlanCalendar = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userData, setUserData] = useState(null);
    const [workPlans, setWorkPlans] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [approvedLeaves, setApprovedLeaves] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    useEffect(() => {
        const fetchWorkPlanHistory = async () => {
            try {
                setLoading(true);
                const data = await getMemberWorkPlanHistory(userId);
                setUserData(data.user);
                setWorkPlans(data.workPlans || []);
                setHolidays(data.holidays || []);
                setApprovedLeaves(data.approvedLeaves || []);
                setError(null);
            } catch (err) {
                setError("Failed to load work plan history");
                console.error("Error fetching work plan history:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchWorkPlanHistory();
    }, [userId]);

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        
        const days = [];
        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(null);
        }
        
        // Add days of the month
        // Create dates using UTC to ensure consistent date-only comparison with holidays
        for (let i = 1; i <= daysInMonth; i++) {
            // Create date at noon UTC to avoid timezone edge cases
            // This ensures the date represents the calendar day regardless of client timezone
            const dayDate = new Date(Date.UTC(year, month, i, 12, 0, 0));
            days.push(dayDate);
        }
        
        return days;
    };

    const getWorkPlanForDate = (date) => {
        const dateString = formatDateString(date);
        return workPlans.find(plan => {
            const planDate = new Date(plan.created_at);
            const planDateString = formatDateString(planDate);
            return planDateString === dateString;
        });
    };

    // Helper function to format date as YYYY-MM-DD for comparison
    // Uses UTC methods to ensure consistent comparison with database date-only fields
    // This prevents timezone mismatches between client and server
    const formatDateString = (date) => {
        // Use UTC methods to ensure date-only comparison works across all timezones
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Check if date is a weekend (Saturday or Sunday)
    const isWeekend = (date) => {
        // Use UTC day of week to match UTC-based date formatting
        const dayOfWeek = date.getUTCDay();
        return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
    };

    // Check if date is a company holiday
    const isHoliday = (date) => {
        if (!holidays || holidays.length === 0) return false;
        
        const dateString = formatDateString(date);
        const dateYear = date.getUTCFullYear();
        
        return holidays.some((holiday) => {
            if (!holiday.date) return false;
            
            // Supabase DATE fields are returned as strings in YYYY-MM-DD format (date-only, no timezone)
            // Normalize to YYYY-MM-DD for comparison
            let holidayDateStr;
            if (typeof holiday.date === 'string') {
                // Remove time component if present (format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
                holidayDateStr = holiday.date.split('T')[0].split(' ')[0].trim();
            } else {
                // If it's a Date object, format it using UTC to match calendar date formatting
                const holidayDate = new Date(holiday.date);
                const year = holidayDate.getUTCFullYear();
                const month = String(holidayDate.getUTCMonth() + 1).padStart(2, '0');
                const day = String(holidayDate.getUTCDate()).padStart(2, '0');
                holidayDateStr = `${year}-${month}-${day}`;
            }
            
            // Extract year from holiday date string for comparison
            const holidayYear = parseInt(holidayDateStr.split('-')[0]);
            
            // Check year matches (helps with filtering)
            // Use UTC year for consistency with date formatting
            if (holiday.year && holiday.year !== dateYear && holidayYear !== dateYear) {
                return false;
            }
            
            // Compare normalized date strings (both in YYYY-MM-DD format using UTC)
            return holidayDateStr === dateString;
        });
    };

    // Check if date falls within an approved leave range
    const isApprovedLeave = (date) => {
        const dateString = formatDateString(date);
        return approvedLeaves.some(leave => {
            return dateString >= leave.start_date && dateString <= leave.end_date;
        });
    };

    // Get the display status for a day based on priority logic
    // Returns an array of statuses to support combined statuses (e.g., work plan + weekend)
    const getDayStatus = (date, workPlan) => {
        const statuses = [];
        
        // Check for work plan
        if (workPlan) {
            statuses.push('workPlan');
        }
        
        // Check for weekend
        if (isWeekend(date)) {
            statuses.push('weekend');
        }
        
        // Check for holiday
        if (isHoliday(date)) {
            statuses.push('holiday');
        }
        
        // Check for approved leave (only if no work plan)
        if (!workPlan && isApprovedLeave(date)) {
            statuses.push('approvedLeave');
        }
        
        // If no statuses found, it's LOP
        if (statuses.length === 0) {
            statuses.push('lop');
        }
        
        return statuses;
    };

    const formatMonthYear = (date) => {
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const navigateMonth = (direction) => {
        const newDate = new Date(currentMonth);
        newDate.setMonth(newDate.getMonth() + direction);
        setCurrentMonth(newDate);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <BunnyLoader 
                    size="large" 
                    text="Loading calendar..."
                    animation="bounce"
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error">{error}</div>
            </div>
        );
    }

    const days = getDaysInMonth(currentMonth);

    return (
        <DefaultPageLayout>
            <div className="calendar-container">
                <div className="header">
                    <div className="header-left">
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherArrowLeft />}
                            onClick={() => navigate(`/work-plan-history/${userId}`)}
                        >
                            Back
                        </Button>
                        <div className="flex items-center gap-3">
                            <h1 className="text-heading-2">Work Plan Calendar</h1>
                        </div>
                    </div>
                </div>

                <div className="calendar-header">
                    <Button
                        variant="neutral-tertiary"
                        onClick={() => navigateMonth(-1)}
                    >
                        Previous
                    </Button>
                    <h2 className="text-heading-3">{formatMonthYear(currentMonth)}</h2>
                    <Button
                        variant="neutral-tertiary"
                        onClick={() => navigateMonth(1)}
                    >
                        Next
                    </Button>
                </div>

                <div className="calendar-grid">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="calendar-day-header">
                            {day}
                        </div>
                    ))}
                    
                    {days.map((date, index) => {
                        const workPlan = date ? getWorkPlanForDate(date) : null;
                        const totalHours = workPlan ? workPlan.tasks.reduce((sum, task) => sum + (task.hours || 0), 0) : 0;
                        const dayStatuses = date ? getDayStatus(date, workPlan) : [];
                        
                        // Build className with all statuses (convert camelCase to kebab-case)
                        let dayClassName = 'calendar-day';
                        if (!date) {
                            dayClassName += ' empty';
                        } else if (dayStatuses.length > 0) {
                            dayStatuses.forEach(status => {
                                // Convert camelCase to kebab-case: workPlan -> work-plan, approvedLeave -> approved-leave
                                const statusClass = status
                                    .replace(/([A-Z])/g, '-$1')
                                    .toLowerCase();
                                dayClassName += ` status-${statusClass}`;
                            });
                        }
                        
                        // Determine what to display
                        const hasWorkPlan = dayStatuses.includes('workPlan');
                        const isWeekendDay = dayStatuses.includes('weekend');
                        const isHolidayDay = dayStatuses.includes('holiday');
                        const isApprovedLeaveDay = dayStatuses.includes('approvedLeave');
                        const isLop = dayStatuses.includes('lop');
                        
                        return (
                            <div key={index} className={dayClassName}>
                                {date && (
                                    <>
                                        <div className="day-number">{date.getUTCDate()}</div>
                                        <div className="day-content">
                                            {hasWorkPlan ? (
                                                <>
                                                    <div className="hours">{totalHours} hours</div>
                                                    <div className="tasks">{workPlan.task_count} tasks</div>
                                                    <Badge variant={workPlan.is_closed ? "success" : "warning"}>
                                                        {workPlan.is_closed ? "Completed" : "In Progress"}
                                                    </Badge>
                                                    {isWeekendDay && <div className="no-plan">Week Off</div>}
                                                    {isHolidayDay && <div className="no-plan">Holiday</div>}
                                                </>
                                            ) : isWeekendDay ? (
                                                <div className="no-plan">Week Off</div>
                                            ) : isApprovedLeaveDay ? (
                                                <div className="no-plan">On Approved Leave</div>
                                            ) : isHolidayDay ? (
                                                <div className="no-plan">Holiday</div>
                                            ) : (
                                                <div className="no-plan">No work plan present - LOP</div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </DefaultPageLayout>
    );
};

export default WorkPlanCalendar;
